import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { IoIosRepeat } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import Pagination from '../../../components/pagination/Pagination';
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import DateComp from '../../../components/dateComponent/DateComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu';
import { useGetManagerTeamQuery } from '../../../services/authApi';
import { useGetManagerTeamLookupQuery } from '../../../services/GeneralApi';
import { constraintConfig } from '../../../constraintConfig';
import labelKey from "../../localization/label.json";
import UpdateUserRequestCreditCard from './UpdateUserRequestCreditCard';
import CreditCardRequestUserDetailModal from './CreditCardRequestUserDetailModal';
import UserDetailModal from '../users/UserDetailModal';
import { Link } from 'react-router-dom'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import { KTSVG } from '../../../_metronic/helpers'
import useColumnVisibility from '../../customHooks/useColumnVisibility'
import CreditCardRequestStatusComp from '../../../components/StatusComp/CreditCardRequestStatusComp'

const CC_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: String(constraintConfig.ccCardRequestStatus.Requested), label: 'Requested' },
    { value: String(constraintConfig.ccCardRequestStatus.RequestApproved), label: 'RequestApproved' },
    { value: String(constraintConfig.ccCardRequestStatus.RequestRejected), label: 'RequestRejected' },
    { value: String(constraintConfig.ccCardRequestStatus.CreditCardOrdered), label: 'CreditCardOrdered' },
    { value: String(constraintConfig.ccCardRequestStatus.Cancel), label: 'Cancel' },
    { value: String(constraintConfig.ccCardRequestStatus.RequestExpired), label: 'RequestExpired' },
];

const COLUMN_IDS = [
    'sr', 'firstName', 'department', 'designation', 'cellPhone', 'createDT', 'userType',
    'expenseType', 'location', 'spendingLimit', 'division', 'ccRequestedDate', 'ccApprovedDate',
    'ccRejectedDate', 'ccOrderedDate', 'modifiedOn', 'modifiedBy', 'status', 'actions',
] as const;

const CreditCardRequestsUsers = () => {
    const { roleID, userAccountID } = useAppSelector(selectAuth);
    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'creditCardRequests' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchStr, setSearchStr] = useState('');
    const [sortBy, setSortBy] = useState('modifiedOn');
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortedColumn, setSortedColumn] = useState('modifiedOn');

    const [filterByUserId, setFilterByUserId] = useState<string>('');
    const [creditCardRequestStatusId, setCreditCardRequestStatusId] = useState<string>('');
    const [ccModifiedFrom, setCcModifiedFrom] = useState('');
    const [ccModifiedTo, setCcModifiedTo] = useState('');

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailRequestId, setDetailRequestId] = useState<number | null>(null);
    const [showUserDetailModal, setShowUserDetailModal] = useState(false);
    const [userDetailData, setUserDetailData] = useState<any>(null);

    const { data: teamLookup } = useGetManagerTeamLookupQuery({ managerId: userAccountID }, { skip: !userAccountID });

    const userOptions = useMemo(() => {
        const rows = teamLookup?.result || [];
        return [
            { value: '', label: 'All Users' },
            ...rows.map((u: any) => ({
                value: String(u.id ?? u.ID),
                label: u.value ?? u.Value ?? String(u.id ?? u.ID),
            })),
        ];
    }, [teamLookup]);

    const { data, isFetching: isLoading, isSuccess, refetch } = useGetManagerTeamQuery(
        {
            pageNumber: page,
            pageSize: pageSize,
            sortBy: sortBy,
            sortOrder: sortOrder,
            searchStr: searchStr,
            filterByStatus: '',
            managerId: userAccountID,
            filterByDepartment: '',
            filterByDesignation: '',
            creditCardRequestStatusId: creditCardRequestStatusId || undefined,
            ccModifiedFrom: ccModifiedFrom || undefined,
            ccModifiedTo: ccModifiedTo || undefined,
            filterByUserId: filterByUserId || undefined,
        },
        { skip: !userAccountID }
    );

    const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
    const [editData, setEditData] = useState({});

    const editUserHandler = () => {
        setShowUpdateUserModal((prev) => !prev);
    };

    const openDetail = (orgUserCreditCardRequestId?: number) => {
        if (!orgUserCreditCardRequestId) {
            toast.warning('No Corporate credit card request detail available for this user');
            return;
        }
        setDetailRequestId(orgUserCreditCardRequestId);
        setShowDetailModal(true);
    };

    const openUserDetail = (userAccountId?: number) => {
        if (!userAccountId) {
            toast.warning('User detail is not available for this row');
            return;
        }
        setUserDetailData(userAccountId);
        setShowUserDetailModal(true);
    };

    const handlePageSizeChange = (e: any) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(1);
    };

    const handlePageClick = (e: any) => {
        setPage(e.selected + 1);
    };

    const handleSearchChange = (e: any) => {
        setSearchStr(e.target.value);
        setPage(1);
    };

    const handleSort = (property: string) => {
        setSortBy(property);
        if (sortedColumn === property) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortedColumn(property);
            setSortOrder('asc');
        }
    };

    const handleDateRangeChange = (_event: any, picker: any) => {
        setCcModifiedFrom(moment(picker.startDate).format('YYYY-MM-DD'));
        setCcModifiedTo(moment(picker.endDate).format('YYYY-MM-DD'));
        setPage(1);
    };

    const handleCancelDateSelection = () => {
        setCcModifiedFrom('');
        setCcModifiedTo('');
        setPage(1);
    };

    const [prevModalState, setPrevModalState] = useState({ showUpdateUserModal: false });

    useEffect(() => {
        setPrevModalState({ showUpdateUserModal });
    }, [showUpdateUserModal]);

    useEffect(() => {
        if (prevModalState.showUpdateUserModal && !showUpdateUserModal) {
            refetch();
        }
    }, [showUpdateUserModal, prevModalState.showUpdateUserModal, refetch]);

    useEffect(() => {
        setPage(1);
    }, [searchStr, filterByUserId, creditCardRequestStatusId, ccModifiedFrom, ccModifiedTo]);

    const handleRefresh = () => {
        refetch().then(() => {
            if (isSuccess) toast.success('Data Refresh successfully!');
        });
    };

    const clearFilters = () => {
        setFilterByUserId('');
        setCreditCardRequestStatusId('');
        setCcModifiedFrom('');
        setCcModifiedTo('');
        setSearchStr('');
        setPage(1);
    };

    const isVisible = (columnId: string) => hiddenColumns.includes(columnId);

    const visibleColCount = useMemo(() => {
        const dataCols = COLUMN_IDS.filter((id) => id !== 'actions' && hiddenColumns.includes(id)).length;
        return dataCols + 1;
    }, [hiddenColumns]);

    const renderSortHeader = (property: string, label: string, className = 'min-w-120px') => (
        <th className={className} onClick={() => handleSort(property)} style={{ cursor: 'pointer' }}>
            <div className='table-arrow'>
                {label}
                {sortedColumn === property ? (sortOrder === 'desc' ? <FaArrowDown /> : <FaArrowUp />) : ''}
            </div>
        </th>
    );

    const dateRangeValue = ccModifiedFrom && ccModifiedTo
        ? `${moment(ccModifiedFrom).format('MM/DD/YYYY')} - ${moment(ccModifiedTo).format('MM/DD/YYYY')}`
        : 'MM/DD/YYYY';

    const columnToggles: { id: string; label: string }[] = [
        { id: 'sr', label: labelKey.sr },
        { id: 'firstName', label: labelKey.name },
        { id: 'department', label: labelKey.department },
        { id: 'designation', label: labelKey.designation },
        { id: 'cellPhone', label: labelKey.cellPhone },
        { id: 'createDT', label: labelKey.createdDate },
        { id: 'userType', label: 'User Type' },
        { id: 'expenseType', label: 'Allowed Expense Type' },
        { id: 'location', label: 'Office Location' },
        { id: 'spendingLimit', label: 'Corp CC Limit' },
        { id: 'division', label: 'Division' },
        { id: 'ccRequestedDate', label: 'Corporate Credit Card Requested' },
        { id: 'ccApprovedDate', label: 'Corporate Credit Card Approved' },
        { id: 'ccRejectedDate', label: 'Corporate Credit Card Rejected' },
        { id: 'ccOrderedDate', label: 'Corporate Credit Card Ordered' },
        { id: 'modifiedOn', label: 'Modified On' },
        { id: 'modifiedBy', label: 'Modified By' },
        { id: 'status', label: 'Status' },
        { id: 'actions', label: labelKey.action },
    ];

    return (
        <>
            <UpdateUserRequestCreditCard
                show={showUpdateUserModal}
                handleClose={() => setShowUpdateUserModal(false)}
                data={editData}
            />
            <CreditCardRequestUserDetailModal
                show={showDetailModal}
                handleClose={() => {
                    setShowDetailModal(false);
                    setDetailRequestId(null);
                }}
                orgUserCreditCardRequestId={detailRequestId}
            />
            <UserDetailModal
                show={showUserDetailModal}
                handleClose={() => {
                    setShowUserDetailModal(false);
                    setUserDetailData(null);
                }}
                data={userDetailData}
            />
            <Tooltip id="refreshData" place="top" />
            <Tooltip id="viewDetail" place="top" />
            <Tooltip id="userDetail" place="top" />
            <Tooltip id="edit-user" place="top" />

            <div className={`card`}>
                <div className='card-header border-0 flex-column align-items-stretch pt-4'>
                    <Alert variant="primary" className="mb-3">
                        <p className='fs-6 mb-0'>
                            Displays your team members and their corporate credit card request status, Corp CC Limit, division, office location, and request timeline. Use filters to find users and submit or re-request a credit card when the previous request was rejected, cancelled, or expired.
                        </p>
                    </Alert>
                    <div className='d-flex flex-wrap gap-3 align-items-end mb-3'>
                        <div style={{ minWidth: 220 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>User:</label>
                            <SearchSelect
                                options={userOptions}
                                value={userOptions.find(o => o.value === filterByUserId) || userOptions[0]}
                                onChange={(e: any) => setFilterByUserId(e?.value != null ? String(e.value) : '')}
                                placeholder='All Users'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 220 }}>
                            <label className='form-label fs-6 fw-bold text-dark'>Credit Card Request Status:</label>
                            <SearchSelect
                                options={CC_STATUS_OPTIONS}
                                value={CC_STATUS_OPTIONS.find(o => o.value === creditCardRequestStatusId) || CC_STATUS_OPTIONS[0]}
                                onChange={(e: any) => setCreditCardRequestStatusId(e?.value != null ? String(e.value) : '')}
                                placeholder='All Statuses'
                                isClearable
                            />
                        </div>
                        <div style={{ minWidth: 260 }}>
                            <DateRangeComp
                                label='Date Range'
                                startDate={ccModifiedFrom ? moment(ccModifiedFrom) : moment().subtract(6, 'days')}
                                endDate={ccModifiedTo ? moment(ccModifiedTo) : moment()}
                                onApply={handleDateRangeChange}
                                onCancel={handleCancelDateSelection}
                                value={dateRangeValue}
                            />
                        </div>
                        <button type='button' className='btn btn-sm btn-light' onClick={clearFilters}>Clear</button>
                    </div>

                    <div className='d-flex justify-content-between align-items-center'>
                        <div
                            data-tooltip-id="refreshData"
                            data-tooltip-content='Refresh Data'
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            onClick={handleRefresh}
                        >
                            <IoIosRepeat className='h-20px w-20px' />
                        </div>
                        <div className='card-title mb-0'>
                            <SearchBarComponent
                                className='me-4'
                                placeholder='User...'
                                value={searchStr}
                                onChange={handleSearchChange}
                            />
                            {roleID === constraintConfig.roleID.role2 && (
                                <Link to='/newuserrequest' className='btn btn-sm btn-primary'>
                                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                                    New User Request
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
                <div className='card-body py-3'>
                    <div className='table-responsive table-height'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    {isVisible('sr') && (
                                        <th className='min-w-40px rounded-start text-center'>{labelKey.sr}</th>
                                    )}
                                    {isVisible('firstName') && renderSortHeader('firstName', labelKey.name)}
                                    {isVisible('department') && renderSortHeader('department', labelKey.department)}
                                    {isVisible('designation') && renderSortHeader('designation', labelKey.designation)}
                                    {isVisible('cellPhone') && renderSortHeader('cellPhone', labelKey.cellPhone, 'min-w-140px')}
                                    {isVisible('createDT') && renderSortHeader('createDT', labelKey.createdDate, 'min-w-120px text-center')}
                                    {isVisible('userType') && renderSortHeader('userType', 'User Type')}
                                    {isVisible('expenseType') && renderSortHeader('expenseType', 'Allowed Expense Type', 'min-w-140px')}
                                    {isVisible('location') && renderSortHeader('location', 'Office Location')}
                                    {isVisible('spendingLimit') && renderSortHeader('spendingLimit', 'Corp CC Limit', 'min-w-100px text-end')}
                                    {isVisible('division') && renderSortHeader('division', 'Division', 'min-w-100px')}
                                    {isVisible('ccRequestedDate') && renderSortHeader('ccRequestedDate', 'Corporate Credit Card Requested', 'min-w-120px text-center')}
                                    {isVisible('ccApprovedDate') && renderSortHeader('ccApprovedDate', 'Corporate Credit Card Approved', 'min-w-120px text-center')}
                                    {isVisible('ccRejectedDate') && renderSortHeader('ccRejectedDate', 'Corporate Credit Card Rejected', 'min-w-120px text-center')}
                                    {isVisible('ccOrderedDate') && renderSortHeader('ccOrderedDate', 'Corporate Credit Card Ordered', 'min-w-120px text-center')}
                                    {isVisible('modifiedOn') && renderSortHeader('modifiedOn', 'Modified On', 'min-w-120px text-center')}
                                    {isVisible('modifiedBy') && renderSortHeader('modifiedBy', 'Modified By', 'min-w-120px')}
                                    {isVisible('status') && renderSortHeader('status', 'Status', 'min-w-100px')}
                                    <th className='min-w-160px rounded-end pe-4 text-end border-0'>
                                        {isVisible('actions') && labelKey.action}
                                        <TableSettingMenu>
                                            {columnToggles.map((col) => (
                                                <TextField
                                                    key={col.id}
                                                    rightLabel={col.label}
                                                    type="checkbox"
                                                    onChange={() => handleToggleColumn(col.id)}
                                                    checked={isVisible(col.id)}
                                                    fieldClass='mb-4'
                                                />
                                            ))}
                                        </TableSettingMenu>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading && (
                                    <>
                                        {data?.result?.data?.length > 0 ? (
                                            data.result.data.map((row: any, index: any) => (
                                                <tr key={row.orgUserCreditCardRequestId || row.userAccountID || index}>
                                                    {isVisible('sr') && (
                                                        <td className='text-center'>{(page - 1) * pageSize + index + 1}</td>
                                                    )}
                                                    {isVisible('firstName') && (
                                                        <td>
                                                            <Link to={`/user-profile/expense-items/${row.userAccountID}`}>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                                        <div className="symbol-label fs-3 bg-light-info text-info text-capitalize">{row?.firstName?.charAt(0)}</div>
                                                                    </div>
                                                                    <div className="d-flex flex-column">
                                                                        <span className="text-gray-800 fw-semibold mb-1 text-capitalize">{row?.firstName} {row?.middleName} {row?.lastName}</span>
                                                                        <span className='text-lowercase'>{row?.email}</span>
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        </td>
                                                    )}
                                                    {isVisible('department') && <td>{row?.department?.title || 'N/A'}</td>}
                                                    {isVisible('designation') && <td>{row?.designation?.title || 'N/A'}</td>}
                                                    {isVisible('cellPhone') && <td>{row?.cellPhone || 'N/A'}</td>}
                                                    {isVisible('createDT') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.createDT} />
                                                        </td>
                                                    )}
                                                    {isVisible('userType') && <td>{row?.userType?.value || 'N/A'}</td>}
                                                    {isVisible('expenseType') && <td>{row?.expenseType?.value || 'N/A'}</td>}
                                                    {isVisible('location') && <td>{row?.location?.value || 'N/A'}</td>}
                                                    {isVisible('spendingLimit') && (
                                                        <td className="text-end">
                                                            {row?.spendingLimit != null && Number(row.spendingLimit) > 0
                                                                ? <CurrencyComp amount={row.spendingLimit} />
                                                                : 'N/A'}
                                                        </td>
                                                    )}
                                                    {isVisible('division') && <td>{row?.division?.title || 'N/A'}</td>}
                                                    {isVisible('ccRequestedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccRequestedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccApprovedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccApprovedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccRejectedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccRejectedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('ccOrderedDate') && (
                                                        <td className='text-center'>
                                                            <DateComp formattedDate={row?.ccOrderedDate} />
                                                        </td>
                                                    )}
                                                    {isVisible('modifiedOn') && (
                                                        <td className='text-center'>
                                                            <DateTimeComp formattedDate={row?.ccRequestModifiedOn || row?.modifiedOn} />
                                                        </td>
                                                    )}
                                                    {isVisible('modifiedBy') && <td className='text-capitalize'>{row?.ccRequestModifiedBy || 'N/A'}</td>}
                                                    {isVisible('status') && (
                                                        <td>
                                                            <CreditCardRequestStatusComp
                                                                statusId={row?.creditCardCardRequest?.id}
                                                                statusTitle={row?.creditCardCardRequest?.title}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        {isVisible('actions') && (
                                                            <div className='d-flex align-items-center justify-content-end gap-2'>
                                                                <button
                                                                    type='button'
                                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                                                    data-tooltip-id='viewDetail'
                                                                    data-tooltip-content='View Corporate credit card request detail'
                                                                    onClick={() => openDetail(row?.orgUserCreditCardRequestId)}
                                                                >
                                                                    <i className='fas fa-eye'></i>
                                                                </button>
                                                                {(() => {
                                                                    const statusId = Number(row?.creditCardCardRequest?.id ?? 0);
                                                                    const isDisabled = statusId > 0
                                                                        && statusId !== constraintConfig.ccCardRequestStatus.RequestRejected
                                                                        && statusId !== constraintConfig.ccCardRequestStatus.Cancel
                                                                        && statusId !== constraintConfig.ccCardRequestStatus.RequestExpired;
                                                                    return (
                                                                        <div
                                                                            data-tooltip-id="edit-user"
                                                                            data-tooltip-content={isDisabled ? 'Credit card request already in progress' : 'Request Credit Card'}
                                                                            className={`btn btn-sm ${isDisabled ? 'btn-secondary' : 'btn-primary'}`}
                                                                            style={isDisabled ? { pointerEvents: 'none', opacity: 0.65 } : undefined}
                                                                            onClick={() => {
                                                                                if (isDisabled) return;
                                                                                setEditData(row);
                                                                                editUserHandler();
                                                                            }}
                                                                        >
                                                                            Request Credit Card
                                                                        </div>
                                                                    );
                                                                })()}
                                                                <div
                                                                    data-tooltip-id='userDetail'
                                                                    data-tooltip-content='User Details'
                                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                                                    onClick={() => openUserDetail(row?.userAccountID)}
                                                                >
                                                                    <KTSVG
                                                                        path='/media/icons/duotune/arrows/arr064.svg'
                                                                        className='svg-icon-3'
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleColCount}><NoRecordFound /></td>
                                            </tr>
                                        )}
                                    </>
                                )}
                                {isLoading && (
                                    <tr>
                                        <td colSpan={visibleColCount} className="text-center"><LoadingComponent /></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        totalResult={data?.result?.totalRecords === 0 ? 0 : ((data?.result?.pageNumber - 1) * data?.result?.pageSize) + 1}
                        toResult={data?.result?.totalRecords === 0 ? 0 : Math.min(data?.result?.pageNumber * data?.result?.pageSize, data?.result?.totalRecords)}
                        ofResult={data?.result?.totalRecords}
                        onChange={handlePageSizeChange}
                        pageSize={pageSize}
                        onPageChange={handlePageClick}
                        pageCount={data?.result?.totalPages || 0}
                    />
                </div>
            </div>
        </>
    )
}

export default CreditCardRequestsUsers